/**
 * Single source of truth for the PROPORTIONS shared by the procedural 3D
 * renderers and their 2D elevation schematics.
 *
 * Why this file exists: the 3D renderers (`furnitureRenderers.js`,
 * `storageRenderers.js`) and the hand-authored 2D schematics
 * (`src/components/elevations/schematics/*`) each draw the same furniture and
 * historically inlined the same magic numbers twice. When one side changed the
 * other silently drifted. Both sides now import these constants, and a snapshot
 * + bbox/subtype consistency test (`proceduralSchematicSync.test.jsx`) fails if
 * they ever disagree.
 *
 * Units are METERS (the renderer contract). Schematics render in their own
 * meter-space viewBox, so they consume these numbers directly; anything in
 * inches is derived via `IN_TO_M`.
 *
 * `*Frac` values are fractions of an object dimension (width/height/depth).
 * `*Min` / `*Max` are clamp bounds in meters. The renderer and schematic apply
 * the SAME `clamp(value*frac, min, max)` so they land on the same number.
 *
 * This module is PURE (no Three.js, no React) so both sides — and the test —
 * can import it freely.
 */

import {
  classifyStorage,
  STORAGE_SUB_TYPES,
  STORAGE_THRESHOLDS,
} from './classifyStorage';
// SURROUND / WALL-HUNG SINK sections (bottom of file): the pipeline-side
// numbers these objects are BUILT with, so the 3D and 2D surfaces read the
// producer's own values rather than a copy.
import {
  FRIDGE_SIDE_PANEL_THICKNESS,
  KITCHEN_COUNTERTOP_THICKNESS,
} from '@shared-lib/kitchen-assembly/constants';
import { wallHungSinkDeckDepth } from '@shared-lib/vanity-assembly/wallHungSink';
import { hoodFormOf } from '@shared-lib/fixture-types/hoodForm';
import {
  boxHoodApronM,
  BOX_HOOD_LEDGE_PROUD_IN,
  CURVED_WOOD_HOOD,
} from '@shared-lib/cabinet-runs/woodHoods';

// Re-export the storage classifier + thresholds so consumers have ONE import
// for "everything that decides storage proportions". The thresholds live in
// classifyStorage.js and are NOT duplicated here.
export { classifyStorage, STORAGE_SUB_TYPES, STORAGE_THRESHOLDS };

export const IN_TO_M = 0.0254;

export const clampSpec = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// ---------------------------------------------------------------------------
// CHAIR — Track-Arm dining chair
// ---------------------------------------------------------------------------
// Thin upholstered seat pad on a thin matte-black metal frame. The two REAR
// legs continue full-height as posts carrying the backrest.
export const CHAIR = {
  // Seat top above the floor: clamp(height * frac, min, max).
  seatHeightFrac: 0.5,
  seatHeightMin: 0.42,
  seatHeightMax: 0.5,
  seatThick: 0.035, // slim tailored seat pad
  // Seat pad footprint as a fraction of the chair footprint. The seat is the
  // chair's widest/deepest element, so this drives the renderer bounding box.
  seatWidthFrac: 0.94,
  seatDepthFrac: 0.94,
  legRadius: 0.011, // thin pin legs / rear posts
  postInset: 0.04, // leg/post centers inset this far from the seat half-extent
  frontLegPenetration: 0.008, // front legs run this far up into the seat
  // Backrest panel carried by the rear posts near the top.
  backPanelHeightFrac: 0.55, // of (chairTop - seatTop)
  backPanelHeightMin: 0.14,
  backPanelHeightMax: 0.24,
  backPanelTopGap: 0.03, // gap from the chair top down to the panel top
  backPanelThick: 0.03,
  backReclineRad: -0.06, // slight backward recline
};

// ---------------------------------------------------------------------------
// TABLE — Track-Arm table
// ---------------------------------------------------------------------------
// A single thin pale-ash top on four thin matte-black metal pin legs tied low
// by slim stretcher rails (no thick apron).
export const TABLE = {
  topThickFrac: 0.05, // top thickness = clamp(height * frac, min, max)
  topThickMin: 0.025,
  topThickMax: 0.04,
  legPenetration: 0.012, // legs run this far up into the top underside
  legInsetFrac: 0.08, // leg inset from the edge = clamp(minFoot * frac, min, max)
  legInsetMin: 0.05,
  legInsetMax: 0.12,
  legRadiusFrac: 0.02, // leg radius = clamp(minFoot * frac, min, max)
  legRadiusMin: 0.011,
  legRadiusMax: 0.02,
  stretcherHeightFrac: 0.28, // stretcher height above floor = legH * frac
  stretcherThickFrac: 1.2, // stretcher thickness = clamp(legR * frac, min, max)
  stretcherThickMin: 0.012,
  stretcherThickMax: 0.02,
};

// ---------------------------------------------------------------------------
// SOFA — Sleek Track-Arm 3-seater
// ---------------------------------------------------------------------------
// Long, low, tailored body: a shallow upholstered plinth between two thin
// squared track arms, a tight low back, three crisp seat + back cushions, all
// floating on four thin matte-black metal pin legs. Greige woven linen.
//
// `sofaProportions(dims)` is the SINGLE derivation of every meter value (arm
// width, base/back heights, cushion bands, leg inset/radius, …). Both the 3D
// renderer and the 2D schematic call it so they can never disagree. All values
// are floor-relative (floor = 0); the renderer offsets by -height/2 itself.
export const SOFA = { seatCount: 3 };

// Seat cushions per class. The size presets in `normalObjectTypes` write
// `attributes.SofaType`, and the 2D floor plan has always drawn from it; this
// is the same vocabulary reaching the 3D geometry.
const SOFA_SEAT_COUNTS = { single: 1, loveseat: 2, triple: 3 };

/**
 * Plan-view layout of a SECTIONAL, in the SAME ratios the 2D floor plan uses
 * (`utils/objectDrawingUtils`, the sofa case) so plan and 3D cannot disagree:
 * an L is a full-width back run plus one side arm, a U is the back run plus
 * two. The opening faces the front (+z), matching the 2D "opening toward the
 * front".
 */
export const SOFA_SECTIONAL_RATIOS = {
  'L-sectional': { sectionWidth: 0.45, sectionDepth: 0.45 },
  'U-sectional': { sectionWidth: 0.3, sectionDepth: 0.4 },
};

export function sofaSectionalRuns({ width, depth }, sofaType) {
  const ratio = SOFA_SECTIONAL_RATIOS[sofaType];
  if (!ratio) return null;

  const sectionWidth = width * ratio.sectionWidth;
  const sectionDepth = depth * ratio.sectionDepth;
  const armDepth = depth - sectionDepth;
  const armZ = -depth / 2 + sectionDepth + armDepth / 2;

  // `back` is which edge of the run carries its back panel.
  const runs = [
    {
      key: 'back',
      w: width,
      d: sectionDepth,
      cx: 0,
      cz: -depth / 2 + sectionDepth / 2,
      back: '-z',
      seats: sofaType === 'U-sectional' ? 2 : 3,
    },
    {
      key: 'right',
      w: sectionWidth,
      d: armDepth,
      cx: width / 2 - sectionWidth / 2,
      cz: armZ,
      back: '+x',
      seats: 1,
    },
  ];
  if (sofaType === 'U-sectional') {
    runs.push({
      key: 'left',
      w: sectionWidth,
      d: armDepth,
      cx: -width / 2 + sectionWidth / 2,
      cz: armZ,
      back: '-x',
      seats: 1,
    });
  }
  return { sectionWidth, sectionDepth, armDepth, runs };
}

export function sofaProportions({ width, height, depth }, sofaType) {
  const legH = clampSpec(height * 0.22, 0.16, 0.2); // tall thin legs (signature)
  const armW = clampSpec(width * 0.06, 0.09, 0.14); // thin squared track arm
  const backThick = clampSpec(depth * 0.1, 0.07, 0.11); // tight low back panel
  const baseH = clampSpec(height * 0.16, 0.1, 0.15); // shallow upholstered plinth
  const baseTop = legH + baseH;
  const baseW = width - armW * 2 + 0.01; // runs between the arms
  const bodyD = depth - 0.04; // base depth (crisp inset from footprint)
  const armD = depth - 0.02; // arms run nearly the full depth

  // Arms + back share ONE low top — the tailored track-arm signature.
  const topY = clampSpec(legH + height * 0.62, baseTop + 0.16, height);
  const armH = topY - legH;
  const armXOffset = width / 2 - armW / 2;

  const innerW = width - armW * 2;
  const backH = topY - baseTop;
  const backZ = -depth / 2 + backThick / 2 + 0.01;

  // Tight seat cushions — shallow, crisp, sit just above the base.
  const seatCushionH = clampSpec(height * 0.11, 0.07, 0.11);
  const seatFrontZ = depth / 2 - 0.05;
  const seatBackZ = -depth / 2 + backThick + 0.01;
  const seatD = seatFrontZ - seatBackZ;
  const seatZ = (seatFrontZ + seatBackZ) / 2;

  // Tight back cushions — thin, upright (no recline).
  const backCushH = clampSpec(backH - 0.05, 0.14, height * 0.42);
  const backCushThick = clampSpec(backThick * 0.7, 0.05, 0.09);
  const backCushZ = -depth / 2 + backThick + backCushThick / 2 + 0.005;
  const backCushBottom = baseTop + seatCushionH - 0.01;

  // Unknown / absent type keeps today's three-seat sofa exactly.
  const seatCount = SOFA_SEAT_COUNTS[sofaType] ?? SOFA.seatCount;
  const areaW = innerW - 0.02;
  const cushW = areaW / seatCount;

  const legInset = armW * 0.55 + 0.02;
  const legR = clampSpec(width * 0.01, 0.011, 0.016);

  return {
    legH,
    armW,
    armD,
    armH,
    armXOffset,
    backThick,
    backZ,
    baseH,
    baseW,
    baseTop,
    bodyD,
    topY,
    innerW,
    backH,
    seatCushionH,
    seatFrontZ,
    seatBackZ,
    seatD,
    seatZ,
    backCushH,
    backCushThick,
    backCushZ,
    backCushBottom,
    seatCount,
    areaW,
    cushW,
    legInset,
    legR,
    sofaType,
    // Null for every non-sectional class, so the existing single-body path is
    // untouched by objects that have never had a type set.
    sectional: sofaSectionalRuns({ width, depth }, sofaType),
  };
}

// ---------------------------------------------------------------------------
// BED — Sleek Track-Arm platform
// ---------------------------------------------------------------------------
// Low crisp upholstered platform on four thin matte-black metal pin legs, a
// mattress sunk slightly into the platform, two crisp pillows + a flat duvet,
// and a thin squared low headboard at the head end (-z). Greige woven linen on
// the upholstered platform + headboard.
//
// `bedProportions(dims)` is the SINGLE derivation shared by renderer + schematic.
// Floor-relative (floor = 0); the renderer offsets by -height/2.
export function bedProportions({ width, height, depth }) {
  const legH = clampSpec(height * 0.2, 0.16, 0.2); // tall thin legs (signature)
  const platformH = clampSpec(height * 0.14, 0.1, 0.16); // low platform
  const platformTop = legH + platformH;

  const mattressH = clampSpec(height * 0.2, 0.16, 0.22);
  const mattressSink = 0.008; // mattress sinks into the platform (no coplanar)
  const mattressTop = platformTop + mattressH - mattressSink;

  const pillowW = clampSpec(width * 0.42, 0.3, 0.55);
  const pillowH = 0.1;
  const pillowD = 0.3;
  const pillowZ = -depth / 2 + pillowD / 2 + 0.08;
  const pillowSink = 0.006;

  const duvetH = 0.05;
  const duvetD = depth * 0.62;
  const duvetZ = depth / 2 - duvetD / 2 - 0.02;

  const hbH = clampSpec(height * 0.4, 0.26, 0.42); // thin squared low headboard
  const hbThick = 0.05;
  const hbZ = -depth / 2 - hbThick / 2 + 0.01;

  const legInset = 0.06;
  const legR = 0.014;

  return {
    legH,
    platformH,
    platformTop,
    mattressH,
    mattressSink,
    mattressTop,
    pillowW,
    pillowH,
    pillowD,
    pillowZ,
    pillowSink,
    duvetH,
    duvetD,
    duvetZ,
    hbH,
    hbThick,
    hbZ,
    legInset,
    legR,
  };
}

// ---------------------------------------------------------------------------
// STORAGE — Sleek Track-Arm casegoods
// ---------------------------------------------------------------------------
// Crisp squared bodies in light wood floating on thin matte-black metal legs,
// flat fronts with thin recessed pulls. `classifyStorage` (imported above)
// picks the sub-type from dimensions; each sub-type has its own proportions.

// Shared construction constants (meters).
export const STORAGE_SHARED = {
  frontThick: 0.018, // ~3/4" door / drawer front slab
  frontProud: 0.006, // fronts stand this far proud of the carcass face
  pullThick: 0.01, // thin recessed-style pull bar projection
  gap: 0.008, // reveal gap between fronts
  boardThick: 0.018, // ~3/4" shelf / carcass board
};

// Per sub-type proportions. `legHeight*` describes the short feet / legs the
// body floats on; `legInset*` the corner inset; counts/spacings drive the
// number of doors/drawers/shelves so the schematic dispatches identically.
export const STORAGE = {
  wardrobe: {
    legHeightFrac: 0.04,
    legHeightMin: 0.06,
    legHeightMax: 0.1,
    legInsetFrac: 0.06,
    legInsetMin: 0.04,
    legInsetMax: 0.08,
    legRadius: 0.012,
    doorCount: 2, // two full-height doors splitting the width
  },
  bookshelf: {
    legHeightFrac: 0.05,
    legHeightMin: 0.05,
    legHeightMax: 0.09,
    legInsetFrac: 0.05,
    legInsetMin: 0.035,
    legInsetMax: 0.06,
    legRadius: 0.011,
    shelfSpacing: 0.32, // target gap between open shelves
    shelfCountMin: 2,
    shelfCountMax: 4,
  },
  // Mounted rather than standing, so no leg fields at all — the side panels
  // run the full height. Shared with the elevation schematic so the two views
  // can't disagree about panel thickness or how many shelves a unit has.
  'wall-shelf': {
    panelThickDepthFrac: 0.125, // panel = min(STORAGE_SHARED.boardThick, depth * this)
    shelfSpacing: 0.3048, // 12" shelf pitch; bays = round(height / spacing)
    shelfCountMin: 1,
  },
  dresser: {
    legHeightFrac: 0.16,
    legHeightMin: 0.1,
    legHeightMax: 0.16,
    legInsetFrac: 0.06,
    legInsetMin: 0.04,
    legInsetMax: 0.07,
    legRadius: 0.012,
    drawerSpacing: 0.22, // target drawer height -> count = round(bodyH / spacing)
    drawerCountMin: 3,
    drawerCountMax: 4,
  },
  console: {
    legHeightFrac: 0.28,
    legHeightMin: 0.1,
    legHeightMax: 0.16,
    legInsetFrac: 0.04,
    legInsetMin: 0.05,
    legInsetMax: 0.09,
    legRadius: 0.012,
    doorCountWideThreshold: 1.5, // width > threshold -> 3 doors else 2
    doorCountNarrow: 2,
    doorCountWide: 3,
  },
  nightstand: {
    legHeightFrac: 0.3,
    legHeightMin: 0.14,
    legHeightMax: 0.2,
    legInsetFrac: 0.08,
    legInsetMin: 0.035,
    legInsetMax: 0.06,
    legRadius: 0.011,
    twoDrawerMinBodyH: 0.4, // bodyH > threshold -> 2 drawers else 1
  },
};

// ---------------------------------------------------------------------------
// Shared count helpers — used by BOTH the renderer and the schematic so the
// number of drawn drawers/doors/shelves can never disagree.
// ---------------------------------------------------------------------------

/** Drawer count for a dresser body of height `bodyH` (meters). */
export function dresserDrawerCount(bodyH) {
  const s = STORAGE.dresser;
  return clampSpec(
    Math.round(bodyH / s.drawerSpacing),
    s.drawerCountMin,
    s.drawerCountMax
  );
}

/** Open-shelf count for a bookshelf interior of height `innerH` (meters). */
export function bookshelfShelfCount(innerH) {
  const s = STORAGE.bookshelf;
  return clampSpec(
    Math.round(innerH / s.shelfSpacing),
    s.shelfCountMin,
    s.shelfCountMax
  );
}

/**
 * Carcass panel thickness for a wall-mounted shelf unit (meters). A standard
 * board, but thinned on a shallow unit so the panels don't eat the depth.
 */
export function wallShelfPanelThickness(depth) {
  const s = STORAGE['wall-shelf'];
  return Math.min(STORAGE_SHARED.boardThick, depth * s.panelThickDepthFrac);
}

/**
 * Number of shelf BAYS in a wall-mounted unit of height `height` (meters).
 * Boards drawn = bays + 1, since the top and bottom boards both count.
 *
 * Shared with the elevation schematic on purpose: derive this separately in
 * the two views and a 6-foot unit can show six shelves in 3D and five on
 * paper for the same object.
 */
export function wallShelfBays(height) {
  const s = STORAGE['wall-shelf'];
  return Math.max(s.shelfCountMin, Math.round(height / s.shelfSpacing));
}

/** Door count for a console of width `width` (meters). */
export function consoleDoorCount(width) {
  const s = STORAGE.console;
  return width > s.doorCountWideThreshold ? s.doorCountWide : s.doorCountNarrow;
}

/** Drawer count for a nightstand body of height `bodyH` (meters). */
export function nightstandDrawerCount(bodyH) {
  return bodyH > STORAGE.nightstand.twoDrawerMinBodyH ? 2 : 1;
}

// ---------------------------------------------------------------------------
// Dorm boards — corkboard / whiteboard / poster / tapestry
// ---------------------------------------------------------------------------
// All four are built by the same `buildBoard`, which insets the printed
// surface by the frame thickness on every side. The elevation schematic draws
// that same inner line, so the number lives here rather than in either
// renderer — a poster framed 0.25" in 3D and 0.5" on paper is exactly the
// drift this module exists to prevent.
export const DORM_BOARD_FRAME_THICK = {
  board: 0.5 * IN_TO_M, // corkboard / whiteboard — buildBoard's default
  poster: 0.25 * IN_TO_M, // thin printed-poster frame
  tapestry: 0, // cloth hangs bare — frameless
};

// ---------------------------------------------------------------------------
// COOKTOP — drop-in burner deck
// ---------------------------------------------------------------------------
// `cooktopHeight` (dimensionPolicy) snaps the deck to a 3/4" slab, so the deck
// itself carries almost no relief. What reads is what stands ON it: a low
// burner head under a cast-iron grate ring, plus a knob per burner. These sit
// ABOVE the object's bounding box in both the 3D mesh and the elevation.
export const COOKTOP_FIVE_BURNER_WIDTH = 36 * IN_TO_M; // 36"+ decks get a centre burner
export const COOKTOP_BURNER_HEAD_H = 0.2 * IN_TO_M; // low cap sitting on the glass
export const COOKTOP_GRATE_TOP = 0.6 * IN_TO_M; // top of the grate above the deck
export const COOKTOP_GRATE_TUBE = 0.12 * IN_TO_M; // ring stock thickness
export const COOKTOP_KNOB_RADIUS = 0.45 * IN_TO_M;
export const COOKTOP_KNOB_H = 0.4 * IN_TO_M;
export const COOKTOP_KNOB_Z_RATIO = 0.4; // knob row's +z offset vs depth (front edge)
export const COOKTOP_KNOB_GAP = 2.4 * IN_TO_M; // center-to-center within the knob strip

/**
 * Knob x-offsets from the deck center: a compact strip of one knob per
 * burner, grouped at the center-front like a real drop-in's control strip.
 * Spreading them across the full width parked the outer knobs directly
 * under the burner grates, which reads as clutter in both views.
 */
export function cooktopKnobOffsets(burnerCount) {
  return Array.from(
    { length: burnerCount },
    (_v, i) => (i - (burnerCount - 1) / 2) * COOKTOP_KNOB_GAP
  );
}

/** Burner head radius for a deck of these dimensions (meters). */
export function cooktopBurnerRadius(width, depth) {
  return Math.min(width, depth) * 0.11;
}

/**
 * Burner centres as [xOffset, zOffset] from the deck centre. Four corners,
 * plus a centre burner once the deck is wide enough.
 *
 * Shared with the elevation schematic on purpose: derive this twice and a 36"
 * deck shows five burners in 3D and four on paper for the same appliance.
 */
export function cooktopBurnerPositions(width, depth) {
  const positions = [
    [-width * 0.24, -depth * 0.18],
    [width * 0.24, -depth * 0.18],
    [-width * 0.24, depth * 0.18],
    [width * 0.24, depth * 0.18],
  ];
  if (width >= COOKTOP_FIVE_BURNER_WIDTH) positions.push([0, 0]);
  return positions;
}

// ---------------------------------------------------------------------------
// BATHTUB — shell with an open basin cavity (bathroomFixtureRenderers.js)
// ---------------------------------------------------------------------------
// Shared with the elevation schematic so the paper cavity matches the mesh:
// walls inset by the shell thickness, basin floor a few inches above the
// shell bottom (real tubs carry a plinth/void under the basin).
export const BATHTUB_WALL_THICKNESS = 1.5 * IN_TO_M;
export function bathtubFloorThickness(height) {
  return Math.min(Math.max(2.5 * IN_TO_M, height * 0.12), height * 0.3);
}

// ---------------------------------------------------------------------------
// VENT HOOD — wall-mounted capture chamber (ventHoodRenderer.js)
// ---------------------------------------------------------------------------
// Shared with the elevation schematic so paper draws the SAME profile the
// mesh extrudes: full-height body, a short vertical lip at the bottom
// front, sloping back to a cabinet-depth top. The width never tapers —
// only the depth does, so a FRONT elevation is a rectangle with the lip
// line and grille, and the canopy read appears in the SIDE view.
export const VENT_HOOD_TOP_DEPTH = 12 * IN_TO_M; // top flush with upper cabinets
export const VENT_HOOD_LIP_HEIGHT = 3 * IN_TO_M; // vertical lip at the bottom front
export const VENT_HOOD_GRILLE_WIDTH_RATIO = 0.85; // grille strip vs body width
export const VENT_HOOD_GRILLE_HEIGHT_RATIO = 0.7; // grille strip vs lip height
export const VENT_HOOD_CHIMNEY_SIDE = 10 * IN_TO_M; // floating duct cover, 10" square

// ---------------------------------------------------------------------------
// WALL-MOUNT CHIMNEY HOOD — pyramid canopy + duct cover to the ceiling
// (ventHoodParts.js / VentHoodChimneyViews.jsx)
// ---------------------------------------------------------------------------
// The SAME `vent-hood` / `range-hood` object as above, selected by
// `dimensions.chimneyHeight > 0` WITHOUT `dimensions.floating` — the wall
// counterpart of the floating island hood, keyed on the one field that
// already means "a duct cover rises from this hood to the ceiling"
// (`ProceduralObject` treats any `chimneyHeight > 0` as ceiling-attached).
// Proportions follow a standard 30"/36" wall-mount chimney hood (Broan
// EW58 / ZLINE KB-30 class): a canopy ~8" tall × 20" deep that tapers in
// BOTH width and depth to a small top platform, under a 10"-square duct
// cover (VENT_HOOD_CHIMNEY_SIDE) in two telescoping sections. The canopy
// fills the object box; the duct cover is render-only and rises ABOVE the
// box by `chimneyHeight` — exactly the floating hood's contract
// (`buildFloatingVentHood`: the chimney is not part of the collision box).
export const CHIMNEY_HOOD = {
  lipHeight: 2 * IN_TO_M, // vertical control/grille band at the canopy bottom
  lipMaxFrac: 0.5, // lip vs canopy height, so a very short canopy stays well-formed
  ductSide: VENT_HOOD_CHIMNEY_SIDE, // 10" square duct cover, back flush to the wall
  ductMaxFrac: 0.6, // duct vs canopy width/depth on a narrow hood (as floating)
  topReveal: 1 * IN_TO_M, // canopy top platform past the duct on each exposed side
  lowerSectionHeight: 12 * IN_TO_M, // lower duct-cover section; the upper telescopes above
  upperInset: 0.375 * IN_TO_M, // upper section sits 3/8" inside the lower, per side
  grilleWidthRatio: VENT_HOOD_GRILLE_WIDTH_RATIO,
  grilleHeightRatio: VENT_HOOD_GRILLE_HEIGHT_RATIO,
};

/**
 * Is this hood a WALL-MOUNT chimney hood — a duct cover to draw, and a wall
 * behind it? Shared dispatch for the renderer and the schematic (both read
 * the object's `dimensions`). A hood with no `chimneyHeight` is the plain
 * canopy above; a floating one is the island hood. The rule is shared-lib's
 * (`hoodFormOf`), so the estimate prices the hood this draws.
 */
export function isWallChimneyHood(dims = {}) {
  return hoodFormOf(dims) === 'chimney';
}

// ---------------------------------------------------------------------------
// VENUS WOOD HOOD — a cabinet box over the range (woodHoodParts.js /
// VentHoodSchematic.jsx). The SAME `vent-hood` object, its build the
// object's `hoodSku` (`@shared-lib/fixture-types/hoodForm.hoodBuildOf`), never
// a dimensions key. A box in the cabinet paint filling the object's box: an
// upper body, two side boards and an arched front valance below it, the
// underside open dark where the insert sits (Venus RHA, catalog p.18). The
// apron's height is shared-lib's (`boxHoodApronM`): the backsplash runs up to
// the insert over it.
// ---------------------------------------------------------------------------
export const WOOD_HOOD = {
  archRiseFrac: 0.5, // the arch's rise vs the apron height
  footFrac: 0.08, // the straight foot either side of the arch vs width
  board: 0.75 * IN_TO_M, // the side, front and apron boards' thickness
  // the ledge over the apron, standing proud of the front and both sides
  // (catalog p.16)
  ledgeThickness: 1.5 * IN_TO_M,
  // shared-lib's: the uppers beside the hood stand this far clear
  ledgeProjection: BOX_HOOD_LEDGE_PROUD_IN * IN_TO_M,
  frontPanels: 3, // framed panels across the front above the ledge
};

/**
 * Every derived number the wood hood needs, computed ONCE for the mesh and the
 * elevation (meters): the apron's height (`valanceHeight`), the arch's rise
 * and its straight foot either side, the boards' thickness, the ledge, and
 * the front panels above it — each `{ x0, x1 }` across the hood (from its
 * left edge) between the side boards, and the height they share.
 */
export function woodHoodLayout({ width, height }) {
  const s = WOOD_HOOD;
  const valanceHeight = boxHoodApronM(height);
  const board = Math.min(s.board, width / 4);
  const ledgeThickness = Math.min(
    s.ledgeThickness,
    (height - valanceHeight) / 4
  );
  const panelWidth = (width - 2 * board) / s.frontPanels;
  return {
    valanceHeight,
    archRise: valanceHeight * s.archRiseFrac,
    foot: Math.max(width * s.footFrac, board),
    board,
    ledgeThickness,
    ledgeProjection: s.ledgeProjection,
    panelHeight: height - valanceHeight - ledgeThickness,
    panels: Array.from({ length: s.frontPanels }, (_, i) => ({
      x0: board + i * panelWidth,
      x1: board + (i + 1) * panelWidth,
    })),
  };
}

// The curved hood's flare: how far out its sides and front have come, as a
// share of the way, `s ** FLARE_POWER` at `s` of the way down from the
// chimney — upright where it leaves the chimney, sweeping out to the band
// (the store's drawings, Venus WRH).
const CURVED_HOOD_FLARE_POWER = 2;

/**
 * Every derived number the CURVED wood hood (Venus WRH) needs, computed ONCE
 * for the mesh and the elevation (meters), from shared-lib's
 * `CURVED_WOOD_HOOD`: the chimney's width, depth and straight top, the
 * flare's height and `flareAt(s)` — its `{ width, depth }` at `s` of the way
 * down from the chimney (0) to the band (1), the back always on the wall —
 * the base band, the boards' thickness and the two moldings, each
 * `{ height, proud }`, standing proud of the band's front and sides.
 */
export function curvedWoodHoodLayout({ width, height, depth }) {
  const c = CURVED_WOOD_HOOD;
  const band = Math.min(c.bandIn * IN_TO_M, height / 3);
  const straight = Math.min(c.chimneyStraightIn * IN_TO_M, height / 6);
  const chimneyWidth = Math.max(
    width - c.chimneyNarrowerIn * IN_TO_M,
    width / 3
  );
  const chimneyDepth = Math.min(c.chimneyDepthIn * IN_TO_M, depth);
  const molding = (m) => ({
    height: Math.min(m.heightIn * IN_TO_M, band / 3),
    proud: m.proudIn * IN_TO_M,
  });
  return {
    band,
    straight,
    flareHeight: Math.max(height - band - straight, 0),
    chimneyWidth,
    chimneyDepth,
    flareAt: (s) => {
      const k = Math.min(Math.max(s, 0), 1) ** CURVED_HOOD_FLARE_POWER;
      return {
        width: chimneyWidth + (width - chimneyWidth) * k,
        depth: chimneyDepth + (depth - chimneyDepth) * k,
      };
    },
    board: Math.min(WOOD_HOOD.board, width / 4),
    cap: molding(c.capMolding),
    base: molding(c.baseMolding),
  };
}

/**
 * Does this hood hide with the CEILING — vanish once the camera rises above
 * its chimney top (`ProceduralObject`)? Only an ISLAND hood: it hangs from
 * the ceiling and goes with it. A wall chimney hood stands on its wall like
 * the cabinets beside it — under a stacked row its chimney stops at the
 * row's top (user ruling 2026-10-21), and hiding it from above would leave a
 * hole between those cabinets in every orbit view.
 */
export function hidesWithCeiling(dims = {}) {
  return dims?.floating === true && (dims.chimneyHeight || 0) > 0;
}

/**
 * Every derived number the wall chimney hood needs, computed ONCE for both
 * views (meters). `chimneyHeight` is the duct cover's rise above the canopy
 * top; the lower section takes the first `lowerSectionHeight` of it and the
 * (inset) upper section the rest, so a short chimney is a single section.
 */
export function chimneyHoodLayout({ width, height, depth, chimneyHeight }) {
  const s = CHIMNEY_HOOD;
  const lip = Math.min(s.lipHeight, height * s.lipMaxFrac);
  const ductWidth = Math.min(s.ductSide, width * s.ductMaxFrac);
  const ductDepth = Math.min(s.ductSide, depth * s.ductMaxFrac);
  const rise = Math.max(0, chimneyHeight || 0);
  const lowerHeight = Math.min(rise, s.lowerSectionHeight);
  const upperHeight = rise - lowerHeight;
  const upperInset = upperHeight > 0 ? s.upperInset : 0;
  return {
    lip,
    ductWidth,
    ductDepth,
    topWidth: Math.min(width, ductWidth + 2 * s.topReveal),
    topDepth: Math.min(depth, ductDepth + s.topReveal),
    lowerHeight,
    upperHeight,
    upperWidth: ductWidth - 2 * upperInset,
    upperDepth: ductDepth - upperInset,
    grilleWidth: width * s.grilleWidthRatio,
    grilleHeight: lip * s.grilleHeightRatio,
  };
}

// ---------------------------------------------------------------------------
// WOOD STOVE — freestanding heating appliance
// ---------------------------------------------------------------------------
// Shared with the elevation schematic so the two views agree on the vertical
// stack: hearth pad -> legs -> body -> cook-top plate -> flue. The hearth
// spreads BEYOND the object box (4" per side, 8" at the door), in both views.
export const WOOD_STOVE_LEG_HEIGHT_RATIO = 0.12;
export const WOOD_STOVE_LEG_THICKNESS_RATIO = 0.08;
export const WOOD_STOVE_TOP_PLATE_RATIO = 0.06;
export const WOOD_STOVE_HEARTH_THICKNESS = 0.0508; // 2", as the fireplace slab
export const WOOD_STOVE_HEARTH_SIDE_MARGIN = 0.1016; // 4" proud each side/back
export const WOOD_STOVE_HEARTH_FRONT_MARGIN = 0.2032; // 8" proud at the door
export const WOOD_STOVE_BODY_RATIO = 0.92; // body width/depth vs the object box
export const WOOD_STOVE_DOOR_WIDTH_RATIO = 0.66; // glass door vs body width
export const WOOD_STOVE_DOOR_HEIGHT_RATIO = 0.6; // glass door vs body height
export const WOOD_STOVE_FLUE_RADIUS_RATIO = 0.09; // flue radius vs min(w, d)
export const WOOD_STOVE_FLUE_HEIGHT_RATIO = 0.28; // flue rise ABOVE the box vs height
export const WOOD_STOVE_FLUE_BACK_OFFSET_RATIO = 0.25; // flue center behind mid, vs depth

// ---------------------------------------------------------------------------
// Dorm theme wall art — shapes AND palettes, because these pieces are drawn
// twice (3D renderer + elevation schematic) and a garland whose flags
// alternate in one view and don't in the other is the exact drift this file
// exists to prevent.
// ---------------------------------------------------------------------------

/** Pennant garland: felt triangles clipped to a slack cord. */
export const PENNANT_SPACING = 7 * IN_TO_M; // cord distance between flag apexes
export const PENNANT_THICKNESS = 0.12 * IN_TO_M; // felt, essentially flat
// Sag is a fraction of HEIGHT, not span: the box has to contain the droop plus
// a full flag below it. Deriving it from span let a wide banner sag further
// than its own box was tall.
export const PENNANT_DROOP_FRAC = 0.28;
export const PENNANT_FLAG_WIDTH_FRAC = 0.62; // flag width vs its own height
export const PENNANT_CURVE_SEGMENTS = 16;
export const PENNANT_CORD_RADIUS = 0.12 * IN_TO_M;
export const PENNANT_CORD_COLOR = 0x6b5a44; // jute twine

/** Vinyl record wall: 12" LPs hung in a grid. */
export const LP_DIAMETER = 12 * IN_TO_M;
export const LP_GAP = 2 * IN_TO_M;
export const LP_LABEL_FRAC = 0.36; // label disc vs record diameter
export const LP_MAX_THICKNESS = 0.5 * IN_TO_M;
export const VINYL_BLACK = 0x141414;

/**
 * Muted outdoorsy felt — pine, moss, bark, canvas, rust. Alternating along the
 * cord is what makes a garland read as a garland rather than as bunting.
 */
export const PENNANT_FELT_COLORS = [
  0x3f5f43, 0x7d8c5c, 0x6b5136, 0xd9cdb4, 0xa8552f,
];

/**
 * Label colours from the 80s palette the research kept naming: hot pink,
 * electric blue, neon green, sunset orange.
 */
export const LP_LABEL_COLORS = [0xe8407a, 0x2f7fd8, 0x54c05a, 0xe8863a];

/**
 * A theme's accent LEADS a piece's own colour rotation rather than replacing
 * it.
 *
 * `planWallArt` stamps `attributes.themeColor` on EVERY piece it emits, and
 * every preset defines an accent — so a renderer written as
 * `themeColor ?? PALETTE[i % PALETTE.length]` never reaches its palette at all.
 * Both the pennant garland and the record wall were built that way and rendered
 * every flag, and every label, one flat colour. The pennant's own comment calls
 * that outcome wrong ("bunting", not a garland).
 *
 * Substituting for slot 0 rather than prepending keeps the rotation length, so
 * a theme whose accent already sits in the palette doesn't get it twice.
 */
export function accentLedPalette(themeColor, palette) {
  if (!themeColor) return palette;
  return [themeColor, ...palette.slice(1)];
}

/** How many records fit across a span, always at least one. */
export function recordsAcross(span) {
  return Math.max(1, Math.floor((span + LP_GAP) / (LP_DIAMETER + LP_GAP)));
}

/** Wall oar: a decorative paddle, blade at -X, T-grip at +X. */
export const OAR_BLADE_LENGTH_FRAC = 0.3; // blade occupies the outer ~30%
export const OAR_GRIP_LENGTH_FRAC = 0.08; // stubby T-grip at the far end
export const OAR_SHAFT_RADIUS_FRAC = 0.16; // vs height, before halving
export const OAR_THICKNESS_FRAC = 0.22; // vs height
export const OAR_GRIP_HEIGHT_FRAC = 0.34; // T-grip bar vs height
export const OAR_BAND_WIDTH_FRAC = 0.18; // painted band vs shaft length
export const OAR_BAND_RADIUS_SCALE = 1.12; // band proud of the shaft
// A cubic bezier peaks well short of its control points (~3/4 of the way for
// this pair), so the controls are pushed past the target width to make the
// blade actually reach the oar's declared height instead of looking spindly.
// BOTH drawings must use it or the 2D blade comes out visibly narrower.
export const OAR_BLADE_BEZIER_OVERSHOOT = 1.34;
export const OAR_WOOD = 0xd8c3a0; // sun-bleached ash
export const OAR_ACCENT = 0x4a7c9b; // painted band — weathered nautical blue

/** Rattan wall fan: two concentric rings plus radial slats. */
export const FAN_SLAT_COUNT = 24;
export const FAN_RING_TUBE_FRAC = 0.045; // ring thickness vs radius
export const FAN_HUB_RADIUS_FRAC = 0.3; // inner ring vs radius
export const RATTAN = 0xc9a877; // natural woven cane

/** Neon sign: a squiggle on a dark backing plate. */
export const NEON_DEFAULT = 0xff3fa4; // hot pink
export const NEON_TUBE_RADIUS = 0.55 * IN_TO_M;
export const NEON_PLATE_COLOR = 0x191922;
export const NEON_PLATE_DEPTH_FRAC = 0.4;
export const NEON_PLATE_MAX_THICK = 0.75 * IN_TO_M;
export const NEON_PANEL_WIDTH_FRAC = 0.86; // curve span vs panel width
export const NEON_PANEL_HEIGHT_FRAC = 0.8;

/**
 * The neon squiggle, as plain {x, y} points centred on the origin with +y UP.
 *
 * Pure and shared: the 3D renderer feeds these to a CatmullRomCurve3 and the
 * elevation schematic turns them into an SVG path. A lightning-bolt /
 * soundwave shape, abstract enough to work at any aspect ratio unlike
 * lettering. Two hand-copied sine loops would drift the moment either side
 * retuned the amplitude.
 */
export const NEON_WAVE_CYCLES = 2.5;
export const NEON_WAVE_AMP_FRAC = 0.32;
export const NEON_WAVE_TILT_FRAC = 0.1;
export function neonWavePoints(width, height, steps = 24) {
  const halfW = width / 2;
  const amp = height * NEON_WAVE_AMP_FRAC;
  const pts = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    pts.push({
      x: -halfW + t * width,
      y:
        Math.sin(t * Math.PI * NEON_WAVE_CYCLES * 2) * amp +
        (t - 0.5) * height * NEON_WAVE_TILT_FRAC,
    });
  }
  return pts;
}

// ---------------------------------------------------------------------------
// Catenary sag — PURE, so the 2D schematics can draw the same slack curve the
// 3D renderers hang. `catenaryStrand.js` wraps these into THREE.Vector3s and a
// tube sweep; a schematic consumes the plain {x, y} points directly.
//
// This lives here rather than in catenaryStrand.js because that module imports
// three, and an elevation schematic must not. Without it the pennant garland's
// sag would be hand-copied into the schematic — the drift this file exists to
// prevent, on the one piece whose whole shape IS the curve.
// ---------------------------------------------------------------------------

/**
 * Parabolic approximation of a hanging strand. `x` is measured from the
 * midpoint, `halfW` is half the span, `droop` is the sag at centre.
 * Returns a NEGATIVE offset (the strand hangs below its endpoints).
 */
export function catenaryY(x, halfW, droop) {
  const t = x / halfW;
  return -droop * (1 - t * t);
}

/**
 * Sample the strand across its span as plain points, endpoints at y = 0 and
 * centre at y = -droop. `+y` is UP, as everywhere in the renderer contract;
 * an SVG schematic negates it.
 */
export function catenarySagPoints(width, droop, segments) {
  const halfW = width / 2;
  const pts = [];
  for (let i = 0; i <= segments; i++) {
    const x = -halfW + (i / segments) * width;
    pts.push({ x, y: catenaryY(x, halfW, droop) });
  }
  return pts;
}

/** Straight-line distance between two {x, y} points. */
const dist2D = (a, b) => Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2);

/** Total polyline length. Summed segment-by-segment, in order. */
export function polylineLength(points) {
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) {
    total += dist2D(points[i], points[i + 1]);
  }
  return total;
}

/**
 * Arc-length distances for items strung at a fixed spacing, first one half a
 * spacing in so the row is centred on the strand rather than starting at its
 * end. Runs to the end of the strand.
 */
export function evenSpacedDistances(points, spacing) {
  const total = polylineLength(points);
  const out = [];
  for (let d = spacing * 0.5; d <= total; d += spacing) out.push(d);
  return out;
}

/**
 * Arc-length distances for a FIXED count of items spread evenly, skipping the
 * very ends: fractions 1/(n+1) … n/(n+1) of total length.
 *
 * This is why the walker takes a distance list rather than a spacing — a photo
 * string hangs exactly four cards however long it is, so its spacing is an
 * output, not an input.
 */
export function fractionalDistances(points, count) {
  const total = polylineLength(points);
  const out = [];
  for (let n = 1; n <= count; n++) out.push((total * n) / (count + 1));
  return out;
}

/**
 * How many items `fractionalDistances` can place across a span before they
 * overlap — its inverse. That function spreads `n` items at fractions
 * `n/(n+1)`, i.e. with `n + 1` gaps, so `n` fits when `span / (n + 1) >=
 * itemWidth`. Floored at 1: a strand too short for even one item still draws
 * one rather than nothing.
 *
 * `span` is the HORIZONTAL chord, not the arc length, because items overlap
 * left-to-right. The two are not interchangeable on a slack strand whose sag is
 * a fixed size: the photo string droops 5.6in whatever its width, so at 12in
 * its arc runs 43% ahead of its chord and an arc-length count returns three
 * cards where one fits.
 *
 * Not `recordsAcross` (above): that packs items edge-to-edge from one end and
 * needs `n` gaps, not `n + 1`.
 */
export function fractionalCapacity(span, itemWidth) {
  if (!(itemWidth > 0)) return 1;
  return Math.max(1, Math.floor(span / itemWidth) - 1);
}

/**
 * Walk the polyline and invoke `cb(x, y, index)` at each arc-length distance in
 * `distances` (which must be ascending). `baseY` is added to every y.
 *
 * Distances past the end of the strand are never reached — that is what bounds
 * `evenSpacedDistances`, whose caller does not know the arc length.
 */
export function walkPolyline(points, distances, cb, { baseY = 0 } = {}) {
  let arcLen = 0;
  let ti = 0;
  for (let i = 0; i < points.length - 1 && ti < distances.length; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    const segLen = dist2D(p1, p2);
    const segStart = arcLen;
    arcLen += segLen;

    while (ti < distances.length && distances[ti] <= arcLen) {
      // A zero-length segment (degenerate span) would divide to NaN and put
      // every mesh at the origin. Three of the four original copies guarded it.
      const frac = segLen > 0 ? (distances[ti] - segStart) / segLen : 0;
      cb(p1.x + (p2.x - p1.x) * frac, p1.y + (p2.y - p1.y) * frac + baseY, ti);
      ti++;
    }
  }
}

/**
 * Sag of a hanging strand as a fraction of its declared height. Fairy lights,
 * the photo string and the vine all hang from the top of their box with the
 * same slack; the pennant garland deliberately differs
 * (PENNANT_DROOP_FRAC) because it has a full flag to fit BELOW the cord.
 */
export const HANGING_DROOP_FRAC = 0.7;
// SURROUND — fridge / oven / laundry (two jamb panels flanking a slot)
// ---------------------------------------------------------------------------
// One renderer (`fridgeSurroundRenderer`) and one schematic
// (`FridgeSurroundSchematic`) serve `fridge-surround`, `oven-surround` and
// `laundry-surround`: the SAME two full-height side panels, at different
// proportions. The panel thickness is written on the object by both
// producers (`buildFridgeSurround` and `buildLaundrySurround`) as
// `dimensions.sidePanelThickness`, always FRIDGE_SIDE_PANEL_THICKNESS; when it
// is missing (gallery card, malformed data) both surfaces fall back to that
// same constant so they cannot disagree.
export const SURROUND_PANEL_THICKNESS = FRIDGE_SIDE_PANEL_THICKNESS;
// Air gap on each face of a drawn panel so its outer face doesn't share a
// plane with the adjacent cabinet, nor its inner face with the machine body
// (z-fighting). 3D-only: the drawn panel is `thickness - 2 * gap`; the 2D
// elevation draws the nominal thickness (a 3 mm difference is invisible).
export const SURROUND_PANEL_FACE_GAP = 0.0015;

/** Nominal jamb thickness for a surround, from the object's dimensions. */
export function surroundPanelThickness(dimensions = {}) {
  const t = dimensions.sidePanelThickness;
  return typeof t === 'number' && t > 0 ? t : SURROUND_PANEL_THICKNESS;
}

// ---------------------------------------------------------------------------
// WALL-HUNG SINK — compact one-piece porcelain basin, no cabinet
// ---------------------------------------------------------------------------
// The vanity assembly's fallback for a cabinet too small to hold any real sink
// (`shared-lib/vanity-assembly/wallHungSink.js`, which also owns the unit
// height / max width and the faucet-deck depth rule, shared through
// `wallHungSinkDeckDepth`). Proportions after a 20"–24" wall-hung lavatory
// (e.g. Kohler Brenham): a 4½" bowl, ½" porcelain shell, and a shroud under
// the bowl that is narrower and shallower than the basin and tapers back to
// the wall.
export const WALL_HUNG_SINK_SHELL_T = 0.5 * IN_TO_M; // bowl wall + floor
export const WALL_HUNG_SINK_BOWL_MAX_DEPTH = 4.5 * IN_TO_M; // rim to bowl floor
export const WALL_HUNG_SINK_BOWL_MIN_BELOW = 1.5 * IN_TO_M; // unit below the bowl floor, at least
export const WALL_HUNG_SINK_SHROUD_INSET_SIDE = 0.5 * IN_TO_M; // shroud narrower, each side
export const WALL_HUNG_SINK_SHROUD_INSET_FRONT = 3 * IN_TO_M; // shroud set back at its top
export const WALL_HUNG_SINK_SHROUD_TAPER = 1.5 * IN_TO_M; // extra set-back at its bottom

/**
 * The unit's profile, measured DOWN from the rim (y) and FORWARD from the
 * wall plane (z) — the frame a side elevation draws in. The 3D renderer
 * recentres these on the object origin.
 *
 *   basinBottomY  underside of the bowl floor: the basin body ends here and
 *                 the shroud starts
 *   deckZ         front edge of the faucet deck (= back inner face of the bowl)
 *   shroudTopZ    front face of the shroud where it meets the basin underside
 *   shroudBottomZ front face of the shroud at the bottom of the unit
 */
export function wallHungSinkProfile({ height, depth }) {
  const bowlDepth = Math.min(
    WALL_HUNG_SINK_BOWL_MAX_DEPTH,
    height - WALL_HUNG_SINK_BOWL_MIN_BELOW
  );
  const shroudTopZ = depth - WALL_HUNG_SINK_SHROUD_INSET_FRONT;
  return {
    bowlDepth,
    deckDepth: wallHungSinkDeckDepth(depth),
    basinBottomY: bowlDepth + WALL_HUNG_SINK_SHELL_T,
    shroudTopZ,
    shroudBottomZ: shroudTopZ - WALL_HUNG_SINK_SHROUD_TAPER,
  };
}

// ---------------------------------------------------------------------------
// LIGHTING — wall sconce, vanity light bar, flush-mount ceiling light
// ---------------------------------------------------------------------------
// Lives in `lightingSpecs.js` (pure) to keep this file under its size
// ceiling; re-exported here so consumers keep ONE import for proportions.
export {
  WALL_SCONCE,
  wallSconceProportions,
  VANITY_LIGHT,
  vanityLightBulbCount,
  vanityLightProportions,
  CEILING_LIGHT,
  ceilingLightProportions,
  HUNG_LIGHT,
  hungLightProportions,
} from './lightingSpecs';

// ---------------------------------------------------------------------------
// CABINET — lower / upper box cabinets (cabinetRenderer.js)
// ---------------------------------------------------------------------------
// The box cabinet's numbers live in shared-lib beside the front layout they
// build (`@shared-lib/cabinet-runs/cabinetFrontSpecs`), because the estimate
// counts the pulls it bills off the same fronts. Re-exported here so every
// renderer and schematic keeps reading them from this one file.
export {
  CABINET,
  CABINET_HARDWARE,
} from '@shared-lib/cabinet-runs/cabinetFrontSpecs';

// ---------------------------------------------------------------------------
// SHOWER HARDWARE — showerhead + shower controls
// ---------------------------------------------------------------------------
// Real-product sizes shared by `showerRenderers.js` (3D) and the
// Showerhead / ShowerControls schematics (2D). Both sides consume them
// through the pure layout functions in `showerLayout.js`, which fit the
// parts into whatever box the pipeline assigns (wall plane at z = -depth/2,
// +z into the shower). Sizes are trade-standard: a 1/2" NPS shower arm
// (0.84" OD) on a 2-3/4" escutcheon, fixed heads 5"–8" across, slide-bar
// handhelds on a 1" bar, and 6"–7" round / rectangular valve trims.
export const SHOWERHEAD = {
  armPipeRadius: 0.42 * IN_TO_M, // 1/2" NPS pipe, 0.84" OD
  flangeRadius: 1.375 * IN_TO_M, // 2-3/4" wall escutcheon
  flangeThick: 0.25 * IN_TO_M,
  armRunMin: 1 * IN_TO_M, // straight run out of the wall before the bend
  dropAngleMax: Math.PI / 4, // a standard arm drops 45°; steeper in a tall box
  headDiaMax: 8 * IN_TO_M, // fixed heads run 5"–8" (Kohler Forte 5.5", 8" rain-style)
  headThick: 1 * IN_TO_M,
  faceThick: 0.1 * IN_TO_M, // spray plate proud of the rim
  // Spray face: a light satin plate inside a chrome rim, with rubber nozzles
  // in rings (fractions of the plate radius, nozzles per ring). The nozzles
  // stand only `nozzleProud` past the plate, so the head's box is unchanged.
  plateRadiusFrac: 0.82,
  nozzleRings: [
    [0, 1],
    [0.38, 6],
    [0.68, 12],
  ],
  nozzleRadiusFrac: 0.055,
  nozzleProud: 0.0005,
  rainHeadDiaMax: 10 * IN_TO_M, // wall-arm rain heads 8"–10"
  rainHeadThick: 0.5 * IN_TO_M,
  barRadius: 0.5 * IN_TO_M, // 1" slide bar, domed at both ends (within its length)
  barStandoff: 1.5 * IN_TO_M, // bar centre off the wall (bracket depth)
  bracketRadius: 0.5 * IN_TO_M,
  bracketInset: 1 * IN_TO_M, // brackets this far in from the bar ends
  bracketFlangeRadius: 0.8 * IN_TO_M, // escutcheon where a bracket meets the wall
  // The holder: a clamp ring round the bar, a stub out to a sleeve the wand
  // sits in — a cradle, not a washer.
  holderClampRadiusAdd: 0.1 * IN_TO_M, // the clamp ring stands this proud of the bar
  holderClampLen: 1 * IN_TO_M,
  holderSleeveRadius: 0.65 * IN_TO_M,
  holderSleeveLen: 1.1 * IN_TO_M,
  wandLen: 9 * IN_TO_M, // handheld wand, head included
  wandLenMaxFrac: 0.85, // never longer than 85% of the box height
  wandRadius: 0.5 * IN_TO_M, // 1" grip
  wandHeadDiaMax: 3.5 * IN_TO_M,
  wandHeadThick: 0.6 * IN_TO_M,
  wandGripFrac: 0.4, // holder grips the wand 40% up from its bottom
  wandHeadJoinFrac: 0.75, // the wand runs into the head to 75% of its radius from the centre
  // The handle is not a pipe with a puck on the end: over its last `wandNeckLen`
  // the round grip flattens and widens into the head — a neck as wide as
  // `wandNeckHalfWidth` either side and no thicker than the head — and at
  // its foot a conical swivel nut tapers from the grip down to the hose.
  wandNeckLen: 2 * IN_TO_M,
  wandNeckHalfWidth: 0.75 * IN_TO_M,
  wandNutRadius: 0.55 * IN_TO_M,
  wandNutLen: 0.75 * IN_TO_M,
  // The wand leans forward off the bar at a holder's natural angle — never
  // further to fill a deep box, which stood the head a hand's width off the
  // wall — and less only when the box is too shallow for it.
  wandTilt: (12 * Math.PI) / 180,
  wandTiltMin: (3 * Math.PI) / 180,
  // A hand shower's hose to the wall supply elbow it screws into, drawn only
  // where the inspection found that outlet (`hoseOutlet`): the standard 59"
  // hose, ~0.6" OD, on a drop-ear elbow with a 2" flange standing ~1.5" off
  // the wall.
  hoseLen: 59 * IN_TO_M,
  hoseRadius: 0.3 * IN_TO_M,
  // A metal interlock hose: a spiral of ribs every `hoseRibPitch`, drawn as a
  // bump map round a smooth tube (a mesh ribbed at that pitch would be
  // thousands of rings), on a chrome a shade darker and duller than the
  // fittings, so the flexible hose reads apart from the rigid handle.
  hoseRibPitch: 0.16 * IN_TO_M,
  hoseBumpScale: 0.5,
  elbowRadius: 0.4 * IN_TO_M,
  elbowFlangeRadius: 1 * IN_TO_M,
  elbowDepth: 1.5 * IN_TO_M,
  // A hose leaves each fitting in line with it — straight out of the handle's
  // foot, straight off the elbow — and bends into its hang over about this
  // much of its length, rather than kinking at the fitting: a long easy bend
  // out of the handle, a tighter one off the wall elbow.
  hoseBendLeadHandle: 4 * IN_TO_M,
  hoseBendLeadElbow: 2 * IN_TO_M,
  // The hose is too stiff to fold: the bottom of its loop never bends
  // tighter than this, so a loop between two close fittings bulges out into a
  // rounded U instead of pinching to a V.
  hoseMinBendRadius: 1.2 * IN_TO_M,
};

/**
 * A hose of `length` hanging slack between two points at any heights, as
 * plain `{x, y, z}` points (`a` first, `b` last): the straight run between
 * them dropped by a parabola, `4·sag·t·(1-t)`, with the sag solved so the
 * polyline is `length` long. Taut when the points are already that far apart.
 * A parabola rather than a true catenary, as `catenaryY` above: at a hose's
 * slack the two are indistinguishable.
 *
 * `ends.startDir` / `ends.endDir` (unit vectors) are the directions the hose
 * LEAVES `a` and `b` along — out of a fitting, in line with it. The hang is
 * then strung between points `ends.startLead` / `ends.endLead` out along
 * each, and each end is a quadratic bend from the fitting into the hang, so
 * the hose meets the fitting square instead of at the parabola's own angle.
 *
 * `ends.minBendRadius` keeps the bottom of the loop from pinching: the hang's
 * sides spread apart horizontally toward the bottom, by just enough that the
 * curve there bends no tighter than that. (Its horizontal position is
 * `mid + u·(t−½)·(span + extra·4t(1−t))`, whose radius at the bottom is
 * `(span + extra)² / (8·sag)`; with no extra it is the straight run.)
 *
 * The sag is solved over the whole path, bends and bulge included.
 */
export function hangingHosePoints(a, b, length, segments = 24, ends = {}) {
  const {
    startDir,
    endDir,
    startLead = 0,
    endLead = 0,
    minBendRadius = 0,
  } = ends;
  const out = (p, dir, lead) =>
    dir
      ? {
          x: (p.x || 0) + dir.x * lead,
          y: p.y + dir.y * lead,
          z: p.z + dir.z * lead,
        }
      : { x: p.x || 0, y: p.y, z: p.z };
  const A = out(a, startDir, startLead);
  const B = out(b, endDir, endLead);
  // Horizontal run from A to B; a loop between two points one above the other
  // spreads along x (along the wall), never into it.
  const span = Math.hypot(B.x - A.x, B.z - A.z);
  const u =
    span > 1e-6
      ? { x: (B.x - A.x) / span, z: (B.z - A.z) / span }
      : { x: 1, z: 0 };
  const midX = (A.x + B.x) / 2;
  const midZ = (A.z + B.z) / 2;
  const hang = (sag) => {
    const extra =
      minBendRadius > 0 && sag > 0
        ? Math.max(0, Math.sqrt(8 * sag * minBendRadius) - span)
        : 0;
    const pts = [];
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const bow = 4 * t * (1 - t);
      const h = (t - 0.5) * (span + extra * bow);
      pts.push({
        x: midX + u.x * h,
        y: A.y + (B.y - A.y) * t - sag * bow,
        z: midZ + u.z * h,
      });
    }
    return pts;
  };
  // A quadratic Bézier from `p0` through control `c` to `p1`, `p1` excluded.
  const bend = (p0, c, p1, n = 8) => {
    const pts = [];
    for (let i = 0; i < n; i++) {
      const t = i / n;
      const u = 1 - t;
      pts.push({
        x: u * u * p0.x + 2 * u * t * c.x + t * t * p1.x,
        y: u * u * p0.y + 2 * u * t * c.y + t * t * p1.y,
        z: u * u * p0.z + 2 * u * t * c.z + t * t * p1.z,
      });
    }
    return pts;
  };
  const k = Math.max(1, Math.round(segments / 8)); // hang points each bend replaces
  const at = (sag) => {
    const mid = hang(sag);
    if (!startDir && !endDir) return mid;
    const first = startDir ? bend(a, A, mid[k]) : [];
    const last = endDir ? bend(b, B, mid[segments - k]).reverse() : [];
    const body = mid.slice(
      startDir ? k : 0,
      endDir ? segments - k + 1 : undefined
    );
    return [...first, ...body, ...last];
  };
  const lengthOf = (pts) => {
    let total = 0;
    for (let i = 1; i < pts.length; i++) {
      const p = pts[i - 1];
      const q = pts[i];
      total += Math.hypot(q.x - p.x, q.y - p.y, q.z - p.z);
    }
    return total;
  };
  if (lengthOf(at(0)) >= length) return at(0);
  // the length grows with the sag, so bisect between taut and a sag that is
  // already longer than any hose (half its length straight down and back)
  let lo = 0;
  let hi = length / 2;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (lengthOf(at(mid)) < length) lo = mid;
    else hi = mid;
  }
  return at((lo + hi) / 2);
}

export const SHOWER_CONTROLS = {
  plateThick: 0.25 * IN_TO_M, // escutcheon / trim plate
  plateRoundMaxAspect: 1.25, // squarer boxes get a round escutcheon, taller a rectangular trim
  plateCornerFrac: 0.15, // rectangular trim corner radius vs its short side
  plateGap: 0.5 * IN_TO_M, // between the two escutcheons of a two-handle set
  hubRadius: 0.75 * IN_TO_M, // 1-1/2" handle hub / bonnet
  hubLenMax: 0.75 * IN_TO_M,
  stemRadius: 0.3 * IN_TO_M,
  leverRadius: 0.4 * IN_TO_M, // ~3/4" lever bar (Delta Monitor 14 / Moen Posi-Temp)
  leverLenMax: 4 * IN_TO_M,
  leverAngle: Math.PI / 4, // drawn in the off position, pointing up-right
  crossLenMax: 2.5 * IN_TO_M, // cross-handle span
  crossRadius: 0.3 * IN_TO_M,
  knobRadiusFrac: 0.3, // thermostatic knob radius vs the plate's short side
  knobRadiusAlongFrac: 0.18, // ...and vs its long side
};

// ---------------------------------------------------------------------------
// FAUCETS — kitchen faucet, bathroom sink faucet, tub faucet
// ---------------------------------------------------------------------------
// Lives in `faucetSpecs.js` (pure) to keep this file under its size target.
// One resolver + one profile function per type, read by BOTH the 3D
// renderers (`faucetRenderers.js`) and the elevation schematics, so the
// spout reach, arc, flange, lever and handle spread cannot drift.
export {
  FAUCET_STOCK,
  KITCHEN_FAUCET_VARIANTS,
  SINK_FAUCET_VARIANTS,
  TUB_FAUCET_VARIANTS,
  resolveKitchenFaucetVariant,
  resolveBathroomSinkFaucetVariant,
  resolveTubFaucetVariant,
  kitchenFaucetProfile,
  bathroomSinkFaucetProfile,
  tubFaucetProfile,
} from './faucetSpecs';

// ---------------------------------------------------------------------------
// BAY WINDOW — 25/50/25 projecting bay (agent: bay-window)
// ---------------------------------------------------------------------------
// Lives in `bayWindowSpecs.js` (pure); re-exported here so consumers keep
// ONE import for proportions.
export { BAY_WINDOW, bayWindowProfile } from './bayWindowSpecs';

// ---------------------------------------------------------------------------
// BATHROOM SINK — vanity bowl (bathroomFixtureRenderers.js / SinkSchematic)
// ---------------------------------------------------------------------------
// The bowl is the lower half of an ellipsoid scaled to FILL the object box:
// rim at the box top, deepest point at the box bottom. That is what the
// vanity-assembly seat contract needs (calculateSinkSeatY.js seats the box:
// undermount = box top under the slab, drop-in = box top on the slab, vessel
// = box bottom on the slab), so a bowl using only half the box hung
// height/2 below its slab. In section (front OR side) the bowl is therefore a
// half-ellipse spanning the full extent with ry = the full height, which is
// what the schematic draws.
export const BATHROOM_SINK_DRAIN_RADIUS_RATIO = 0.06; // vs min(width, depth)
export const BATHROOM_SINK_DRAIN_HEIGHT = 0.005; // thin disc on the bowl floor

// ---------------------------------------------------------------------------
// KITCHEN SINK — undermount basin (applianceRenderers.js / KitchenSinkSchematic)
// ---------------------------------------------------------------------------
// The undermount basin IS the object box: an open-top rectangular bowl with
// flat walls and floor, so its section is a rectangle — the drawn side view is
// the box with the drain, not a curved profile.
export const KITCHEN_SINK_DRAIN_RADIUS_RATIO = 0.08; // vs min(width, depth)
// A double bowl (`basinCount: 2`, 2026-10-27): two equal bowls either side of
// a divider this wide, its top `belowRim` under the bowls' rim — so the one
// opening in the counter reads as two bowls, as a real undermount double does.
// An undermount generic's box top is the slab TOP (`kitchenSinkSeatY`), and
// its rim the slab's underside, a standard slab's thickness down: measured
// from the box top, its divider stops under the slab rather than poking up
// into the opening. A farmhouse's bowl top is its own rim.
export const KITCHEN_SINK_DIVIDER = {
  width: 2 * 0.0254,
  belowRim: 1 * 0.0254,
  undermountRimDrop: KITCHEN_COUNTERTOP_THICKNESS,
};

/** The bowls a kitchen sink's bowl area holds: one, or two either side of the divider. */
export const kitchenSinkBowls = ({ width, cx = 0 }, basinCount) => {
  if (basinCount !== 2) return [{ width, cx }];
  const each = (width - KITCHEN_SINK_DIVIDER.width) / 2;
  const off = (each + KITCHEN_SINK_DIVIDER.width) / 2;
  return [
    { width: each, cx: cx - off },
    { width: each, cx: cx + off },
  ];
};

// ---------------------------------------------------------------------------
// OUTLETS — duplex / GFCI / 240V receptacles (electricalDeviceRenderers.js /
// OutletSchematic)
// ---------------------------------------------------------------------------
// Face layout on a single-gang plate. Vertical offsets are fractions of the
// plate HEIGHT measured from its center (positive = up); horizontal ones are
// fractions of the plate WIDTH.
export const OUTLET = {
  plateDepth: 0.005, // the plate is a thin slab against the wall
  duplexFaceOffsetFrac: 0.22, // each receptacle face, above / below center
  gfciFaceOffsetFrac: 0.3, // GFCI faces sit further apart to fit the buttons
  slotHeightFrac: 0.1, // blade slot length
  slotWidth: 0.0022, // blade slot width (m)
  slotSpreadFrac: 0.14, // each blade slot, left / right of center
  groundDropFrac: 0.9, // ground hole below the face center, × slot length
  gfciButtonWidthFrac: 0.28, // TEST / RESET buttons
  gfciButtonHeightFrac: 0.07,
  gfciButtonOffsetFrac: 0.17, // each button, left / right of center
  receptacle240RadiusFrac: 0.38, // round 240V face, vs min(width, height)
};

// ---------------------------------------------------------------------------
// SURFACE OUTLET — receptacle set into a floor or countertop
// (electricalDeviceRenderers.js / SurfaceOutletSchematic)
// ---------------------------------------------------------------------------
// Set INTO a horizontal surface, so the bbox BOTTOM is that surface (the
// opposite of the ceiling devices below). Radii are fractions of
// min(width, depth) / 2; heights are fractions of the object HEIGHT.
export const SURFACE_OUTLET = {
  coverHeightFrac: 0.45, // the flange ring flush with the surface
  lidRadiusFrac: 0.72, // the hinged lid disc, vs the cover radius
  lidHeightFrac: 0.55, // the lid proud of the flange
  slotWidth: 0.0022, // blade slot width (m), same as a wall receptacle
  slotLengthFrac: 0.5, // blade slot length, vs the lid radius
  slotSpreadFrac: 0.3, // each slot, left / right of center, vs lid radius
  radialSegments: 24,
};

// ---------------------------------------------------------------------------
// CEILING ELECTRICAL DEVICES — exhaust fan / smoke-CO detector
// (electricalDeviceRenderers.js / CeilingDeviceSchematics)
// ---------------------------------------------------------------------------
// Both mount face-down on the ceiling, so the bbox top is the ceiling plane.
// Heights are fractions of the object HEIGHT, widths of its width / depth.
export const EXHAUST_FAN = {
  frameHeightFrac: 0.5, // housing flange against the ceiling (top half)
  grilleHeightFrac: 0.5, // slatted grille below it (bottom half)
  grilleInsetFrac: 0.78, // grille span vs the housing, both axes
  slatCount: 6, // slats run along width, stacked across depth
  slatFillFrac: 0.45, // each slat's share of its pitch
};

export const SMOKE_DETECTOR = {
  bodyHeightFrac: 0.8, // tapered body against the ceiling
  bodyBottomRadiusFrac: 0.92, // bottom radius vs top radius (the taper)
  ventHeightFrac: 0.2, // vented center disc below the body
  ventRadiusFrac: 0.45, // vs the body's top radius
  ledRadiusFrac: 0.05, // status LED, vs the body's top radius
  ledOffsetFrac: 0.65, // LED distance from center, vs the body's top radius
};

/**
 * A shower system's accessories (shower systems step 3a): the drawing-only
 * proportions; their sizes are the maker's, in `@shared-lib/shower-systems`.
 * A caddy is boards of `caddyBoardIn` joined at its front by two posts
 * `caddyPostIn` square; a foot shelf's top slopes from its full height at
 * the corner down to `footShelfLipIn` at its front; a grab bar's flange is
 * `grabBarFlangeIn` thick.
 */
export const SHOWER_ACCESSORY = {
  caddyBoardIn: 0.375,
  caddyPostIn: 0.5,
  footShelfLipIn: 1,
  grabBarFlangeIn: 0.25,
};

/**
 * A BUILT-IN microwave's trim kit (`@shared-lib/cabinet-runs/microwaveCabinets`
 * `trimKit` — the user, 2026-11-01: "a stainless steel surround (with vents)
 * around it"): a stainless frame out to the opening, a dark `reveal` between
 * it and the microwave, and a row of vent slots along its top and bottom
 * strips. `thick` is the frame's own depth.
 */
export const TRIM_KIT = {
  reveal: 0.125 * IN_TO_M,
  thick: 0.125 * IN_TO_M,
  vent: {
    w: 2 * IN_TO_M,
    h: 0.25 * IN_TO_M,
    gap: 0.5 * IN_TO_M,
    // clear of the frame's ends, and above and below a slot
    margin: 1 * IN_TO_M,
    edge: 0.25 * IN_TO_M,
  },
};

/**
 * The trim kit round a `width` x `height` microwave, in its face's terms
 * (`microwaveFaceLayout`: `x` about the face's centre, `y` up from its
 * bottom): `frame` its outer `width`, `height` and `y` (its middle above the
 * microwave's). `strips` the four pieces from the reveal out to the frame
 * (top and bottom the whole width), `vents` the slots, centred in each of
 * those two strips that has room for one. The 3D renderer and the elevation
 * draw these rects.
 */
export function trimKitLayout(width, height, frame) {
  const { reveal, vent } = TRIM_KIT;
  const rect = (left, right, bottom, top) => ({
    x: (left + right) / 2,
    y: (bottom + top) / 2,
    w: right - left,
    h: top - bottom,
  });
  const outer = {
    left: -frame.width / 2,
    right: frame.width / 2,
    bottom: height / 2 + (frame.y || 0) - frame.height / 2,
    top: height / 2 + (frame.y || 0) + frame.height / 2,
  };
  const inner = {
    left: -width / 2 - reveal,
    right: width / 2 + reveal,
    bottom: -reveal,
    top: height + reveal,
  };
  const strips = {
    top: rect(outer.left, outer.right, inner.top, outer.top),
    bottom: rect(outer.left, outer.right, outer.bottom, inner.bottom),
    left: rect(outer.left, inner.left, inner.bottom, inner.top),
    right: rect(inner.right, outer.right, inner.bottom, inner.top),
  };
  const span = frame.width - 2 * vent.margin;
  const count = Math.max(
    0,
    Math.floor((span + vent.gap) / (vent.w + vent.gap))
  );
  const run = count * vent.w + (count - 1) * vent.gap;
  const vents = [];
  for (const strip of [strips.top, strips.bottom]) {
    if (strip.h < vent.h + 2 * vent.edge) continue;
    for (let i = 0; i < count; i++)
      vents.push({
        x: -run / 2 + vent.w / 2 + i * (vent.w + vent.gap),
        y: strip.y,
        w: vent.w,
        h: vent.h,
      });
  }
  return { strips, vents };
}

/**
 * A countertop MICROWAVE's face (the dorm microwave, and the microwave a run
 * places in a microwave cabinet — `@shared-lib/cabinet-runs/microwaveCabinets`):
 * the door with its window on the left, the control panel on the right with
 * its display, keypad, stop / start and door-release buttons, and the door's
 * handle. The 3D renderer and the elevation draw THESE rects, so the two
 * cannot disagree. Each rect is `{ x, y, w, h }`: `x` its centre across the
 * face (0 = the face's centre), `y` its centre up from the face's bottom.
 */
export const MICROWAVE_FACE = {
  panelW: 4 * IN_TO_M,
  doorFrame: 0.75 * IN_TO_M,
  handleW: 0.5 * IN_TO_M,
  handleD: 0.6 * IN_TO_M,
  handleHeightFrac: 0.6,
  handleInset: 0.25 * IN_TO_M,
  display: { w: 2.8 * IN_TO_M, h: 1.2 * IN_TO_M },
  keypad: {
    cols: 3,
    rows: 4,
    w: 0.7 * IN_TO_M,
    h: 0.6 * IN_TO_M,
    gapX: 0.15 * IN_TO_M,
    gapY: 0.25 * IN_TO_M,
  },
  bigButton: { w: 1.2 * IN_TO_M, h: 0.8 * IN_TO_M, gap: 0.3 * IN_TO_M },
  release: { w: 2.8 * IN_TO_M, h: 1 * IN_TO_M },
};

export function microwaveFaceLayout(width, height) {
  const f = MICROWAVE_FACE;
  const rect = (x, y, w, h) => ({ x, y, w, h });
  const doorW = width - f.panelW;
  const panelX = width / 2 - f.panelW / 2;
  const k = f.keypad;
  const gridW = k.cols * k.w + (k.cols - 1) * k.gapX;
  const gridH = k.rows * k.h + (k.rows - 1) * k.gapY;
  const contentH = f.display.h + gridH + f.bigButton.h + f.release.h;
  const margin = (height - contentH) / 5;
  const displayY = height - margin - f.display.h / 2;
  const gridTop = displayY - f.display.h / 2 - margin;
  const gridLeft = panelX - gridW / 2;
  const keypad = [];
  for (let r = 0; r < k.rows; r++)
    for (let c = 0; c < k.cols; c++)
      keypad.push(
        rect(
          gridLeft + k.w / 2 + c * (k.w + k.gapX),
          gridTop - k.h / 2 - r * (k.h + k.gapY),
          k.w,
          k.h
        )
      );
  const bigY = gridTop - gridH - margin - f.bigButton.h / 2;
  const bigDx = f.bigButton.w / 2 + f.bigButton.gap / 2;
  const handleH = height * f.handleHeightFrac;
  return {
    door: rect(-f.panelW / 2, height / 2, doorW, height),
    window: rect(
      -f.panelW / 2,
      height / 2,
      doorW - f.doorFrame * 2,
      height - f.doorFrame * 2
    ),
    panel: rect(panelX, height / 2, f.panelW, height),
    display: rect(panelX, displayY, f.display.w, f.display.h),
    keypad,
    stop: rect(panelX - bigDx, bigY, f.bigButton.w, f.bigButton.h),
    start: rect(panelX + bigDx, bigY, f.bigButton.w, f.bigButton.h),
    release: rect(panelX, margin + f.release.h / 2, f.release.w, f.release.h),
    handle: rect(
      width / 2 - f.panelW - f.handleW / 2 - f.handleInset,
      height / 2,
      f.handleW,
      handleH
    ),
  };
}
